/**
 * Loads the Google Identity Services SDK on demand.
 * The client ID comes from a Vite env var.
 */
declare global {
  interface Window {
    google?: any;
  }
}

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

export const providersConfigured = { google: Boolean(GOOGLE_CLIENT_ID) };

const scriptLoads = new Map<string, Promise<void>>();

/** Loads a script once; concurrent callers share the same promise. */
function loadScript(src: string): Promise<void> {
  let pending = scriptLoads.get(src);
  if (!pending) {
    pending = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = src;
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => {
        scriptLoads.delete(src);
        reject(new Error(`Failed to load ${src}`));
      };
      document.head.appendChild(s);
    });
    scriptLoads.set(src, pending);
  }
  return pending;
}

/**
 * Renders Google's button into `el` and calls `onCredential` with an ID token
 * when the user signs in. `isCancelled` lets the caller abandon the render if
 * the component unmounted while the SDK was loading.
 */
export async function mountGoogleButton(el: HTMLElement, onCredential: (idToken: string) => void, isCancelled: () => boolean = () => false) {
  if (!GOOGLE_CLIENT_ID) return;
  await loadScript("https://accounts.google.com/gsi/client");
  if (isCancelled()) return;
  window.google.accounts.id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: (resp: { credential: string }) => onCredential(resp.credential),
    ux_mode: "popup",
    auto_select: false,
  });
  el.replaceChildren(); // never stack two buttons
  const width = Math.min(400, Math.max(200, Math.floor(el.clientWidth || 382)));
  window.google.accounts.id.renderButton(el, { theme: "outline", size: "large", text: "continue_with", shape: "pill", width });
}
